import { EnvKey } from '../models/env.types'

export type SharedGatewayType =
    | 'lk_web'
    | 'lk_web_proxy'
    | 'lk_api'
    | 'lk_sockets'
    | 'assets'
    | 'chat'
    | 'chat_socket'
    | 'collections'

type SharedGatewayObjectType = { [key in SharedGatewayType]: string }

const url = ({
    base,
    prefix = '',
    protocol,
    suffix = ''
}: {
    protocol: 'https' | 'http' | 'wss' | 'ws'
    prefix?: string
    base: string
    suffix?: string
}) => `${protocol}://${prefix ? `${prefix}.` : ''}${base}/${suffix}`

const wss = (base: string, suffix: string) =>
    url({ protocol: 'wss', base, suffix })

const https = (base: string, suffix: string = '') =>
    url({ protocol: 'https', base, suffix })

const localhost = (port: number) =>
    url({ protocol: 'http', base: `localhost:${port}` })

const localhost_ws = (port: number, suffix?: string) =>
    url({ protocol: 'ws', base: `localhost:${port}`, suffix })

const api_livekid_prod = (prefix: string = '', suffix: string = '') =>
    url({ protocol: 'https', prefix, base: 'api-livekid-prod.com', suffix })

const socket_livekid_prod = (prefix: string = '', suffix: string = '') =>
    url({ protocol: 'wss', prefix, base: 'api-livekid-prod.com', suffix })

const api_livekid_dev = (prefix: string = '', suffix: string = '') =>
    url({ protocol: 'https', prefix, base: 'api-livekid-dev.com', suffix })

const socket_livekid_dev = (prefix: string = '', suffix: string = '') =>
    url({ protocol: 'wss', prefix, base: 'api-livekid-dev.com', suffix })

export const localhost_urls: Partial<SharedGatewayObjectType> = {
    lk_api: localhost(3001),
    lk_sockets: localhost_ws(3001, 'v1/socket'),
    chat: localhost(4000),
    chat_socket: localhost_ws(4000, 'v1/socket'),
    assets: localhost(5001)
}
export const rc_urls: Partial<SharedGatewayObjectType> = {
    lk_web: https('rc.app.livekid.pl'),
    lk_web_proxy: https('rc-web-proxy-e461f'),
    lk_api: api_livekid_dev('api.rc'),
    assets: api_livekid_dev('assets.rc'),
    chat: https('stage.chat.theboringdomain.com'),
    lk_sockets: socket_livekid_dev('api.rc', 'v1/socket'),
    chat_socket: wss('stage.chat.theboringdomain.com', 'ws/chat/')
}
export const stage_urls: Partial<SharedGatewayObjectType> = {
    lk_web: https('stage.app.livekid.pl'),
    lk_web_proxy: https('stage-web-proxy.web.app'),
    lk_api: api_livekid_dev('api.staging'),
    assets: api_livekid_dev('assets.staging'),
    chat: https('stage.chat.theboringdomain.com'),
    lk_sockets: socket_livekid_dev('api.staging', 'v1/socket'),
    chat_socket: wss('stage.chat.theboringdomain.com', 'ws/chat/')
}
export const wekita_urls: Partial<SharedGatewayObjectType> = {
    lk_web: https('app.wekita-welt.de'),
    lk_web_proxy: https('web-proxy.wekita-welt.de'),
    lk_api: api_livekid_prod('wekita.api'),
    lk_sockets: socket_livekid_prod('wekita.api', 'v1/socket')
}
export const urls: SharedGatewayObjectType = {
    lk_web: https('app.livekid.com'),
    lk_web_proxy: https('web-proxy.livekid.com'),
    lk_api: api_livekid_prod('{region}.api'),
    assets: api_livekid_prod('assets'),
    chat: https('chat.theboringdomain.com'),
    lk_sockets: socket_livekid_prod('{region}.api', 'v1/socket'),
    chat_socket: wss('chat.theboringdomain.com', 'ws/chat/'),
    collections: https('collections-pln7cqlk4a-uc.a.run.app')
}
export const provideBaseUrls = (api_env: EnvKey): SharedGatewayObjectType => {
    const overrides = (() => {
        switch (api_env) {
            case 'wekita':
                return wekita_urls
            case 'rc':
                return rc_urls
            case 'stage':
                return stage_urls
            case 'localhost':
                return localhost_urls
            default:
                return {}
        }
    })()
    return {
        ...urls,
        ...overrides
    }
}
